import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, MouseEvent, ReactNode } from 'react';
import { useCallback } from 'react';
import { Button } from 'src/components/buttons-and-actions/button';
import { withNestedClickHandler } from 'src/components/data-display/expandable-card';
import type { DateType } from 'src/components/data-display/format-date';
import { FormatDate } from 'src/components/data-display/format-date';
import { XIcon } from 'src/components/media-and-icons/icons';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';

export type TagProps = {
  label: ReactNode;
  value?: ReactNode;
  onClick?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  onRemove?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  testid?: string;
  /**
   * Be **very** careful with this prop as it can break the accessibility of the component.
   * Only do it if you provide an alternative way of achieving the same functionality via keyboard.
   */
  tabIndex?: number;
};

type TagDateRangeProps = {
  label: ReactNode;
  from: string | null;
  to: string | null;
  type?: DateType;
  onClick?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  onRemove?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  testid?: string;
};

export const TagDateRange: FC<TagDateRangeProps> = ({
  from,
  to,
  type,
  label,
  onClick,
  onRemove,
  testid,
}) =>
  !from && !to ? null : (
    <Tag
      label={label}
      value={
        <>
          {from && to && (
            <>
              <FormatDate type={type} value={from} /> -{' '}
              <FormatDate type={type} value={to} />
            </>
          )}
          {from && !to && (
            <>
              from <FormatDate type={type} value={from} />
            </>
          )}
          {!from && to && (
            <>
              to <FormatDate type={type} value={to} />
            </>
          )}
        </>
      }
      onClick={onClick}
      onRemove={onRemove}
      testid={testid}
    />
  );

export const shortenTagText = (str: string | number, numberOfChars = 25) =>
  str.toString().replace(new RegExp(`(.{${numberOfChars}})..+`), '$1...');

type TagCollectionProps = {
  collection: string[];
  label: ReactNode;
  onClick?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  onRemove?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  testid?: string;
};

export const TagCollection: FC<TagCollectionProps> = ({
  collection,
  onClick,
  onRemove,
  label,
  testid,
}) => (
  <Tag
    label={label}
    value={shortenTagText(collection.join(', '), 25)}
    onClick={onClick}
    onRemove={onRemove}
    testid={testid}
  />
);

type TagRangeProps = {
  min: number | string | null;
  max: number | string | null;
  label: ReactNode;
  onClick?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  onRemove?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  testid?: string;
};

export const TagRange: FC<TagRangeProps> = ({
  min,
  max,
  onClick,
  onRemove,
  label,
  testid,
}) =>
  min === null && max === null ? null : (
    <Tag
      label={label}
      value={
        <>
          {min !== null && max !== null && min !== max
            ? `${shortenTagText(min)} - ${shortenTagText(max)}`
            : min !== null &&
              max !== null &&
              min === max &&
              `equals ${shortenTagText(min)}`}
          {min !== null && max === null && `min. ${shortenTagText(min)}`}
          {min === null && max !== null && `max. ${shortenTagText(max)}`}
        </>
      }
      onClick={onClick}
      onRemove={onRemove}
      testid={testid}
    />
  );

const baseLabelStyles = cn(
  `
    flex items-center gap-xs rounded-xs bg-background-surface-default px-sm
    py-xs wrap-anywhere
  `,
  `
    legacy:block legacy:rounded-[1rem] legacy:bg-legacy-brand-light-1
    legacy:px-[1.2rem] legacy:py-0 legacy:text-center legacy:text-[1.3rem]
    legacy:leading-[1.8rem] legacy:tracking-[0.15rem] legacy:text-white
  `
);

const Label: FC<{
  label: ReactNode;
  value?: ReactNode;
  className?: string;
  onClick?: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  tabIndex?: number;
}> = ({ label, value, className, ...props }) => {
  const content = (
    <>
      <span
        className={`
          not-legacy:text-label-sm not-legacy:font-label-semibold
          legacy:uppercase
        `}
      >
        {label}
      </span>
      {value && (
        <>
          {' '}
          <span
            className={cn(
              `
                text-foreground-surface-disabled select-none
                after:content-['|']
              `,
              `
                legacy:text-white
                legacy:after:content-['·']
              `
            )}
            aria-hidden="true"
          />{' '}
          <span
            className={`
              shrink-0
              not-legacy:text-label-sm
            `}
          >
            {value}
          </span>
        </>
      )}
    </>
  );

  if (props.onClick) {
    return (
      <button
        type="button"
        className={cn(
          baseLabelStyles,
          `
            not-legacy:auto-cols-auto not-legacy:grid-flow-col
            not-legacy:auto-rows-auto
          `,
          // Even tho we can't really apply the `active` token status here (too harsh, visually) we still make a change on active for "reactivity"
          `
            cursor-pointer
            hover:bg-background-surface-minimal
            active:bg-background-surface-default
          `,
          `legacy:hover:bg-legacy-brand-light-2`,
          className
        )}
        {...props}
      >
        {content}
      </button>
    );
  }

  return <div className={cn(baseLabelStyles, className)}>{content}</div>;
};

export const Tag: FC<TagProps> = ({
  onClick,
  onRemove,
  label,
  value,
  testid = 'tag-id',
  tabIndex,
}) => {
  const isNewDesignActive = useIsNewDesignActive();

  const clickHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (onClick) {
        onClick(event);
      } else if (onRemove) {
        onRemove(event);
      }
    },
    [onClick, onRemove]
  );

  const removeHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (onRemove) {
        onRemove(event);
      } else if (onClick) {
        onClick(event);
      }
    },
    [onClick, onRemove]
  );

  return (
    <div
      className={cn(
        `
          inline-grid auto-cols-auto grid-flow-col auto-rows-auto content-normal
          items-stretch rounded-xs border border-border-secondary-default
          text-left
        `,
        `legacy:rounded-[1rem] legacy:border-none`
      )}
      data-testid={testid}
    >
      <Label
        label={label}
        value={value}
        {...(onClick && {
          ...withNestedClickHandler(clickHandler),
          tabIndex,
        })}
        // We remove radius on the right when remove button is present
        className={cn(
          onRemove &&
            `
              rounded-r-none!
              legacy:pr-[0.6rem]
            `
        )}
      />

      {onRemove && (
        <Button
          mode="secondary"
          size="small"
          className={cn(
            // We remove radius on the left to mesh with the label
            `rounded-l-none rounded-r-[calc(var(--radius-xs)-1px)] px-sm`,
            `
              legacy:block legacy:items-center legacy:rounded-l-none!
              legacy:rounded-r-[1rem] legacy:border-none
              legacy:bg-legacy-brand-light-1 legacy:py-0 legacy:pr-[1.2rem]!
              legacy:pl-[0.6rem]! legacy:text-center legacy:text-[1.3rem]
              legacy:leading-[1.8rem] legacy:tracking-[0.15rem]
              legacy:text-white
              legacy:hover:bg-legacy-brand-secondary
            `
          )}
          data-testid={`remove-${testid}`}
          {...withNestedClickHandler(removeHandler)}
        >
          {isNewDesignActive ? (
            <XIcon size="xs" aria-label="Remove selection" />
          ) : (
            <FontAwesomeIcon
              icon={faTimes}
              aria-label="Remove selection"
              className="legacy:block"
            />
          )}
        </Button>
      )}
    </div>
  );
};
